import React, { type FunctionComponent, useMemo } from 'react'
import styled from '@emotion/styled'
import { FormattedMessage } from 'react-intl'
import { colors, SVGIcon } from 'ic-snacks'
import { Box } from 'legacy/dash-blocks/box/Box'
import { DashTooltip } from 'legacy/dash-blocks/dash-tooltip/DashTooltip'
import { DashTableTextCell } from 'legacy/dash-blocks/dash-table-text-cell/DashTableTextCell'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import { useDashIntl } from 'intl/intl.hooks'

const AlcoholTooltip = styled(Box)`
  display: inline-block;
  vertical-align: top;
`

const alcoholTooltipAccess = { warehouseFlippers: [Flipper.AlcoholOrderTooltip] }

interface Props {
  status: string
  percent?: number
  alcoholic?: boolean
}

export const OrderListTableStatusCell: FunctionComponent<Props> = ({
  status,
  percent,
  alcoholic,
}) => {
  const intl = useDashIntl()

  const style =
    status === 'Canceled'
      ? ({ fontWeight: 'bold', color: colors.RED_500 } as const)
      : ({ fontWeight: 'bold' } as const)

  const statusInProgress = status === 'In Progress'
  const hasAccess = useAccessControl()
  const showAlcoholTooltip = alcoholic && statusInProgress && hasAccess(alcoholTooltipAccess)

  const renderPercent = useMemo(() => {
    if (percent === undefined || !statusInProgress) return null

    const percentageValue = intl.formatNumber(percent, {
      style: 'percent',
    })

    return ` - ${percentageValue}`
  }, [intl, percent, statusInProgress])

  return (
    <DashTableTextCell overrideStyle={style}>
      {status}
      {renderPercent}
      {showAlcoholTooltip && (
        <AlcoholTooltip marginLeft="xxsmall">
          <DashTooltip target={<SVGIcon name="info" color={colors.GREEN_500} />}>
            <FormattedMessage id="orders.alcoholTooltip" />
          </DashTooltip>
        </AlcoholTooltip>
      )}
    </DashTableTextCell>
  )
}
